import * as React from 'react';
import { FC } from 'react';
import { styled } from '@mui/material/styles';
import { Children, memo } from 'react';
import PropTypes from 'prop-types';
import {
    AppBar as MuiAppBar,
    AppBarProps as MuiAppBarProps,
    Toolbar,
    useMediaQuery,
    Theme,
    Button,
    Box,
    Grid,
    Typography
} from '@mui/material';
import { ComponentPropType, useGetIdentity, useGetOne, useLocales, usePermissions } from 'ra-core';

import { SidebarToggleButton, useTranslate } from 'react-admin';
import { LoadingIndicator } from 'react-admin';
import { UserMenu } from 'react-admin';
import { HideOnScroll } from 'react-admin';
import { TitlePortal } from 'react-admin';
import { LocalesMenuButton } from 'react-admin';
import { useThemesContext } from 'react-admin';
import { ToggleThemeButton } from 'react-admin';
import logo_fff from './../resources/ff_logo.png'
import logo_fbl from './../resources/freeBILL_logo.png'
import { Permissions } from '../authentication/Permissions';
import { BookingPeriod, UserReadModel } from '../GeneratedClients/FreeBillApi';
import { setBookingPeriodToLocalState } from '../users/setBookingPeriodToLocalState';
import { useSearchParams } from 'react-router-dom';
import { apiService } from '../apiService';

/**
 * The AppBar component renders a custom MuiAppBar.
 *
 * @param {Object} props
 * @param {ReactNode} props.children React node/s to be rendered as children of the AppBar
 * @param {string} props.className CSS class applied to the MuiAppBar component
 * @param {string} props.color The color of the AppBar
 * @param {Element | boolean} props.userMenu A custom user menu component for the AppBar. <UserMenu/> component by default. Pass false to disable.
 *
 * @example // add a custom button to the AppBar
 *
 * const MyAppBar = () => (
 *   <AppBar>
 *     <TitlePortal />
 *     <MyCustomButton />
 *   </AppBar>
 * );
 *
 * @example // without a user menu
 *
 * const MyAppBar = () => <AppBar userMenu={false} />;
 */

const fffBaseUrl = process.env.REACT_APP_FREE_FIBU_BASE_URL ? process.env.REACT_APP_FREE_FIBU_BASE_URL : 'http://localhost:10101/fff'

const CustomAppBar: FC<AppBarProps> = memo(props => {
    const {
        alwaysOn,
        children,
        className,
        color = 'secondary',
        open,
        title,
        toolbar = defaultToolbarElement,
        userMenu = DefaultUserMenu,
        container: Container = alwaysOn ? 'div' : HideOnScroll,
        ...rest
    } = props;

    const isXSmall = useMediaQuery<Theme>(theme =>
        theme.breakpoints.down('sm')
    );

    const translate = useTranslate();
    const [activeBookingPeriod, setActiveBookingPeriod] = React.useState<BookingPeriod | undefined>()

    const { isLoading, permissions } = usePermissions<Permissions>()
    const { data: identity, isLoading: isLoadingIdentity} = useGetIdentity()
    //@ts-ignore
    const { data: user, isLoading: userLoading} = useGetOne<UserReadModel>("users", {id: identity?.id}, {enabled: !isLoadingIdentity})
    const [searchParams] = useSearchParams()

    const handleBookingPeriod = async() => {
        var searchParamBookingPeriod = searchParams.get("bookingPeriod")
        var searchParamBookingPeriodStartDate = searchParams.get("bookingPeriodStartDate")
        var searchParamBookingPeriodEndDate = searchParams.get("bookingPeriodEndDate")
        var searchParamsExists = searchParamBookingPeriod && searchParamBookingPeriodEndDate && searchParamBookingPeriodStartDate
        var bookingPeriod: BookingPeriod | undefined = user?.bookingPeriod
        if(searchParamsExists) {
            bookingPeriod = {
                name: searchParamBookingPeriod,
                startDate: searchParamBookingPeriodStartDate,
                endDate: searchParamBookingPeriodEndDate
            }     
            await apiService.UsersApi.changeBookingPeriod({changeBookingPeriodCommand: {userId: user?.id, bookingPeriod: bookingPeriod}})
        }
           
        setBookingPeriodToLocalState(bookingPeriod)
        setActiveBookingPeriod(bookingPeriod)
    }

    React.useEffect(() => {
        handleBookingPeriod()
    }, [user])
    
    if(isLoading || isLoadingIdentity || userLoading) return null

    return (
        <Container className={className}>
            <StyledAppBar
                className={AppBarClasses.appBar}
                color={color}
                {...rest}
            >
                <Toolbar
                    disableGutters
                    variant={isXSmall ? 'regular' : 'dense'}
                    className={AppBarClasses.toolbar}
                >
                    <Grid container alignItems={'center'} justifyContent={'space-between'} >
                        <Grid item xs={5} >
                            <Grid container >
                                <Grid item minWidth={245} display={'flex'} alignItems={'center'} >
                                    <SidebarToggleButton className={AppBarClasses.menuButton} />
                                    {Children.count(children) === 0 ? (
                                        <TitlePortal className={AppBarClasses.title} />
                                    ) : (
                                        children
                                    )}
                                </Grid>
                                <Button 
                                    variant={"text"} 
                                    href={permissions.redirectToFff ? `${fffBaseUrl}/status` : "./#/"}
                                    sx={{":hover": {background: "unset"}}}
                                >
                                    <Box 
                                        component={"img"}
                                        sx={{height: permissions.redirectToFff ? 20 : 28}}
                                        src={permissions.redirectToFff ? logo_fff : logo_fbl}
                                    />
                                </Button>
                            </Grid>
                        </Grid>
                        <Grid item display={'flex'} >
                            {toolbar}
                            {
                                activeBookingPeriod?.name && permissions.fffUser &&
                                <Box pt={1} px={3}>
                                    <Typography variant='body2'>
                                        {translate("resources.invoices.bookingPeriod")}: <strong>{activeBookingPeriod?.name}</strong>
                                    </Typography>
                                </Box>
                            }
                            {typeof userMenu === 'boolean' ? (
                                userMenu === true ? (
                                    <UserMenu />
                                ) : null
                            ) : (
                                userMenu
                            )}
                        </Grid>
                    </Grid>
                </Toolbar>
            </StyledAppBar>
        </Container>
    );
});

const DefaultToolbar = () => {
    const locales = useLocales();
    const { darkTheme } = useThemesContext();
    return (
        <>
            {locales && locales.length > 1 ? <LocalesMenuButton /> : null}
            {darkTheme && <ToggleThemeButton />}
            <LoadingIndicator />
        </>
    );
};

const defaultToolbarElement = <DefaultToolbar />;

CustomAppBar.propTypes = {
    alwaysOn: PropTypes.bool,
    children: PropTypes.node,
    className: PropTypes.string,
    color: PropTypes.oneOf([
        'default',
        'inherit',
        'primary',
        'secondary',
        'transparent',
    ]),
    container: ComponentPropType,
    /**
     * @deprecated
     */
    open: PropTypes.bool,
    toolbar: PropTypes.element,
    userMenu: PropTypes.oneOfType([PropTypes.element, PropTypes.bool]),
};

const DefaultUserMenu = <UserMenu />;

export interface AppBarProps extends Omit<MuiAppBarProps, 'title'> {
    /**
     * This prop is injected by Layout. You should not use it directly unless
     * you are using a custom layout.
     * If you are using the default layout, use `<Layout appBarAlwaysOn>` instead.
     */
    alwaysOn?: boolean;
    container?: React.ElementType<any>;
    /**
     * @deprecated injected by Layout but not used by this AppBar
     */
    open?: boolean;
    /**
     * @deprecated injected by Layout but not used by this AppBar
     */
    title?: string | JSX.Element;
    toolbar?: JSX.Element;
    userMenu?: JSX.Element | boolean;
}

const PREFIX = 'RaAppBar';

export const AppBarClasses = {
    appBar: `${PREFIX}-appBar`,
    toolbar: `${PREFIX}-toolbar`,
    menuButton: `${PREFIX}-menuButton`,
    menuButtonIconClosed: `${PREFIX}-menuButtonIconClosed`,
    menuButtonIconOpen: `${PREFIX}-menuButtonIconOpen`,
    title: `${PREFIX}-title`,
};

const StyledAppBar = styled(MuiAppBar, {
    name: PREFIX,
    overridesResolver: (props, styles) => styles.root,
})(({ theme }) => ({
    [`& .${AppBarClasses.toolbar}`]: {
        padding: `0 ${theme.spacing(1)}`,
        [theme.breakpoints.down('md')]: {
            minHeight: theme.spacing(6),
        },
    },
    [`& .${AppBarClasses.menuButton}`]: {
        marginRight: '0.2em',
    },
    [`& .${AppBarClasses.title}`]: {},
}));

export default CustomAppBar